import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  deleteCategory,
  getListCategories,
} from "../../redux/actions/categoryActions";
import Loader from "../../components/Loader";
import DefaultLayout from "../../layouts/DefaultLayout";
import Alert from "../../components/Alert";
import { baseURLFile } from "../../constants";
import ConfirmationModal from "../../components/ConfirmationModal";
import { deleteOrder } from "../../redux/actions/orderActions";

function CategoryScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const page = searchParams.get("page") || "1";

  const [eventType, setEventType] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loadEvent, setLoadEvent] = useState(false);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const listCategories = useSelector((state) => state.getListCategories);
  const { categories, loadingListCategories, errorListCategories } =
    listCategories;

  const categoryDelete = useSelector((state) => state.deleteCategory);
  const { successCategoryDelete, loadingCategoryDelete, errorCategoryDelete } =
    categoryDelete;

  const redirect = "/";

  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    } else {
      dispatch(getListCategories("0"));
    }
  }, [navigate, userInfo, dispatch, page]);

  useEffect(() => {
    if (successCategoryDelete) {
      dispatch(getListCategories("0"));
    }
  }, [successCategoryDelete]);

  return (
    <DefaultLayout>
      <div className="animate-fadeIn">
        {/* Breadcrumb with modern styling */}
        <div className="flex flex-row text-sm items-center mb-4 text-bodydark transition-all">
          <a href="/dashboard" className="group">
            <div className="flex flex-row items-center text-bodydark hover:text-primary transition-colors duration-200">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-4 h-4 group-hover:scale-110 transition-transform duration-200"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                />
              </svg>
              <span className="mx-1.5 font-medium">Dashboard</span>
            </div>
          </a>
          <span className="mx-2 text-bodydark">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              className="w-3.5 h-3.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
              />
            </svg>
          </span>
          <div className="font-medium text-black">Categories List</div>
        </div>

        {/* Header with title and action button */}
        <div className="flex flex-row justify-between items-center mb-6">
          <div className="text-xl font-bold text-black">Categories</div>
          <div className="flex flex-row items-center justify-end">
            <a
              href="/categories/new-category"
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-medium text-sm rounded-lg px-5 py-2.5 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
                stroke="currentColor"
                className="size-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
              <span>New Category</span>
            </a>
          </div>
        </div>
        {/* Modern Table Card */}
        <div className="rounded-xl border border-stroke bg-whiter px-5 pt-6 pb-2.5 shadow-sm hover:shadow-md transition-shadow duration-300">
          <div className="container mx-auto flex flex-col">
            {loadingListCategories ? (
              <div className="flex justify-center items-center h-80">
                <Loader />
              </div>
            ) : errorListCategories ? (
              <div className="flex justify-center items-center h-80">
                <Alert type={"error"} message={errorListCategories} />
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="bg-gray-2 text-left">
                      <th className="min-w-[80px] py-4 px-4 font-semibold text-black text-sm rounded-l-lg">
                        #
                      </th>
                      <th className="min-w-[120px] py-4 px-4 font-semibold text-black text-sm">
                        Image
                      </th>
                      <th className="min-w-[180px] py-4 px-4 font-semibold text-black text-sm">
                        Name
                      </th>
                      <th className="min-w-[120px] py-4 px-4 font-semibold text-black text-sm">
                        Status
                      </th>
                      <th className="min-w-[120px] py-4 px-4 font-semibold text-black text-sm rounded-r-lg text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories?.map((item, index) => (
                      <tr
                        key={index}
                        className={`hover:bg-gray-1 transition-colors duration-200 group ${
                          index === categories.length - 1 ? "" : "border-b border-stroke"
                        }`}
                      >
                        <td className="py-4 px-4">
                          <span className="text-sm font-medium text-black bg-gray-2 py-1 px-2.5 rounded-md">
                            #{item.id}
                          </span>
                        </td>

                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <img
                              src={baseURLFile + item.image}
                              className="size-12 rounded-lg border-2 border-white shadow-sm object-cover"
                              alt={item.name}
                            />
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          <h5 className="text-sm font-medium text-black">
                            {item.name ?? "---"}
                          </h5>
                        </td>

                        <td className="py-4 px-4">
                          {item.is_active ? (
                            <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
                              <span className="h-2 w-2 rounded-full bg-success"></span>
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full text-xs font-medium bg-danger/10 text-danger border border-danger/20">
                              <span className="h-2 w-2 rounded-full bg-danger"></span>
                              Inactive
                            </span>
                          )}
                        </td>

                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center space-x-3.5">
                            <Link
                              to={"/categories/edit/" + item.id}
                              className="hover:text-primary text-body p-1 rounded-full hover:bg-primary/10 transition-colors duration-200"
                              title="Edit Category"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                              </svg>
                            </Link>

                            <button
                              onClick={() => {
                                setEventType("delete");
                                setCategoryId(item.id);
                                setIsOpen(true);
                              }}
                              className="hover:text-danger text-body p-1 rounded-full hover:bg-danger/10 transition-colors duration-200"
                              title="Delete Category"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        {/* Empty space for pagination if needed */}
        <div className="mt-6"></div>

        {/* Enhanced Confirmation Modal */}
        <ConfirmationModal
          isOpen={isOpen}
          message={
            eventType === "delete"
              ? "Are you sure you want to delete this category?"
              : "Are you sure ?"
          }
          onConfirm={async () => {
            if (eventType === "cancel") {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setCategoryId("");
            } else if (eventType === "delete" && categoryId !== "") {
              setLoadEvent(true);
              dispatch(deleteCategory(categoryId));
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
            } else {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setCategoryId("");
            }
          }}
          onCancel={() => {
            setIsOpen(false);
            setEventType("");
            setLoadEvent(false);
            setCategoryId("");
          }}
          loadEvent={loadEvent}
        />
      </div>
    </DefaultLayout>
  );
}

export default CategoryScreen;
